import React from 'react';
import escapeRegExp from 'lodash/escapeRegExp';

// @ts-ignore
import DatasetIcon from '../../images/icons/dataset.svg';
// @ts-ignore
import PipeIcon from '../../images/icons/pipe.svg';
// @ts-ignore
import GlobalPipeIcon from '../../images/icons/global-pipe.svg';
// @ts-ignore
import SystemIcon from '../../images/icons/system.svg';

import { CommandPaletteSearchTypes } from '../../types/commandPalette.types';

import { TestID } from '../../testID';

import type { InjectedRouter } from 'react-router';
import type {
  CommandPaletteSearchRendererArgs,
  Needle,
  NewEntityNeedle,
} from '../../types/commandPalette.types';

import './DatahubObjectsPalette.css';

type RendererIcons = {
  [key in CommandPaletteSearchTypes]: React.JSX.Element | undefined;
};

const rendererIcons: RendererIcons = {
  [CommandPaletteSearchTypes.DATASET]: <DatasetIcon />,
  [CommandPaletteSearchTypes.PIPE]: <PipeIcon />,
  [CommandPaletteSearchTypes.GLOBAL]: <GlobalPipeIcon />,
  [CommandPaletteSearchTypes.SYSTEM]: <SystemIcon />,
  [CommandPaletteSearchTypes.ALL]: undefined,
};

export const commandPaletteRenderer = (args: CommandPaletteSearchRendererArgs) => (
  <div className="datahub-objects-palette__item" title={`${args.type} ${args.id}`}>
    {rendererIcons[args.type]}
    <span className="visually-hidden datahub-objects-palette-capitalize">{args.type}</span>
    {args.name ? args.name : args.id} {args.name !== args.id && `(ID: ${args.id})`}
  </div>
);

export const makeCreateNewNeedle = (
  newEntityId: string,
  router: InjectedRouter,
  subId: string,
  type: string
): NewEntityNeedle => ({
  item: { id: newEntityId },
  keyMaker: (_id: string) => `makeNew${type}-{_id}`,
  onActivate: () => {
    router.push(`/subscription/${subId}/${type}s/new?id=${newEntityId}`);
  },
  renderer: () => (
    <div className="datahub-objects-palette__item" title={`Make a new ${type}`}>
      <span className="visually-hidden">Make a new {type}</span>
      <span data-testid={`${TestID.CommandPaletteCreateEntity}${type}`}>
        Create a new {type} with id "{newEntityId}"
      </span>
    </div>
  ),
  type: type as CommandPaletteSearchTypes,
});

export const makeGoToNiNeedle = (input: string, router: InjectedRouter, subId: string) => ({
  item: { id: input },
  keyMaker: (_ni: string) => `goToNi${_ni}`,
  onActivate: () => {
    router.push(`/subscription/${subId}/browse/ni/${encodeURIComponent(input)}`);
  },
  renderer: (_ni: { id: string }) => (
    <div
      className="datahub-objects-palette__item"
      title="Make a new pipe"
      data-testid={TestID.CommandPaletteGoToNamespaceIdentifierItem}
    >
      <span className="visually-hidden">Go to namespaced identifier</span>
      <span data-testid={TestID.CommandPaletteGoToNamespaceIdentifierText}>
        Go to namespaced identifier "{_ni.id}"
      </span>
    </div>
  ),
  type: 'go-to-namespaced-identifier' as CommandPaletteSearchTypes,
});

export const makeSearchNeedle = (input: string, router: InjectedRouter, subId: string) => ({
  item: { id: input },
  keyMaker: (_query: string) => `search${_query}`,
  onActivate: () => {
    router.push(`/subscription/${subId}/browse/search?q=${encodeURIComponent(input)}`);
  },
  renderer: (_query: { id: string }) => (
    <div className="datahub-objects-palette__item" title={`Do a search`}>
      <span className="visually-hidden">Do a search</span>
      <span data-testid={TestID.CommandPaletteRecentSearch}>Search for "{_query.id}"</span>
    </div>
  ),
  type: 'search' as CommandPaletteSearchTypes,
});

const SearchNameSpace: React.FC<{ text: string }> = ({ text }) => (
  <div
    className="command-palette__search-type-message"
    data-testid={TestID.CommandPaletteSearchInNamespace}
  >
    Searching {text}:
  </div>
);

export const getSearchTypeMessage = (searchType: CommandPaletteSearchTypes) => {
  switch (searchType) {
    case CommandPaletteSearchTypes.PIPE:
      return <SearchNameSpace text="pipes" />;
    case CommandPaletteSearchTypes.SYSTEM:
      return <SearchNameSpace text="systems" />;
    case CommandPaletteSearchTypes.DATASET:
      return <SearchNameSpace text="datasets" />;
    case CommandPaletteSearchTypes.GLOBAL:
      return <SearchNameSpace text="globals" />;
    default:
      return null;
  }
};

export const getResults = (
  prefix: string,
  searchFor: string,
  haystackGenerator: (p: string) => Generator<Needle>,
  maxMatches: number
) => {
  let needle;
  let results = [];

  const substringRegex = new RegExp(`.*${escapeRegExp(searchFor)}.*`, 'i');
  const haystack = haystackGenerator(prefix);

  do {
    needle = haystack.next();

    if (needle.value && needle.value.matcher(needle.value.item, substringRegex)) {
      results.push(needle.value);
    }
  } while (results.length < maxMatches && !needle.done);
  return results;
};
